import { environment } from '../../../environments/environment';
import { ClicktermConsentStatus } from '../models/clickterm.model';
import { Role, UserAdmin } from '../models/user.model';

export interface MainAccountAgreementState {
  isCustomAgreement: boolean;
  isPendingAgreement: boolean;
  isDeclinedAgreement: boolean;
  noAgreementGenerated: boolean;
  isAcceptedAgreement: boolean;
  isOverdueAgreement: boolean;
  isNoDataAgreement: boolean;
  isNoSignedAgreement: boolean;
  noAgreementSigned: boolean;
  isReAcceptanceRequired: boolean;
  canViewAcceptedAgreement: boolean;
  requiresReAcceptanceAfterVerification: boolean;
}

export interface MainAccountAgreementRenderInfo extends MainAccountAgreementState {
  title: string;
  cssClass: string;
}

export class UserAgreementUtils {
  static getMsaAndDpaAgreementState(user: UserAdmin): MainAccountAgreementState {
    const isMainAccount = user.role === Role.MAIN;

    const msaDpaAgreement = (user.clickwrapAgreementsStatuses || []).find(a => a.clickwrapTemplateId === environment.clicktermMsaDpaTemplateId);
    const isPendingAgreement = isMainAccount && msaDpaAgreement?.status === ClicktermConsentStatus.PENDING;
    const isDeclinedAgreement = isMainAccount && msaDpaAgreement?.status === ClicktermConsentStatus.DECLINED;
    const hasAcceptedAgreement = isMainAccount && msaDpaAgreement?.status === ClicktermConsentStatus.ACCEPTED;
    const isOverdueAgreement = isMainAccount && msaDpaAgreement?.status === ClicktermConsentStatus.OVERDUE;
    const isNoDataAgreement = isMainAccount && msaDpaAgreement?.status === ClicktermConsentStatus.NO_DATA;
    const isReAcceptanceRequired = isMainAccount && msaDpaAgreement?.reAcceptanceRequired === true;
    const requiresReAcceptanceAfterVerification = isReAcceptanceRequired && !!user.companyDetailsVerified;
    const canViewAcceptedAgreement = hasAcceptedAgreement && !requiresReAcceptanceAfterVerification;
    const isAcceptedAgreement = canViewAcceptedAgreement;
    const shouldShowPendingAgreement = isPendingAgreement;
    const noAgreementGenerated = isMainAccount && !msaDpaAgreement;
    const isNoSignedAgreement = isMainAccount && (!msaDpaAgreement || isDeclinedAgreement || isOverdueAgreement || isNoDataAgreement);

    const isCustomAgreement = isMainAccount && !!user.customAgreement;
    const isClicktermFlowEnabled = isMainAccount && !!user.clicktermAgreementFlowEnabled;
    const noAgreementFlowEnabled = !isClicktermFlowEnabled && !isCustomAgreement;

    return {
      isCustomAgreement,
      isPendingAgreement: shouldShowPendingAgreement,
      isDeclinedAgreement,
      isAcceptedAgreement,
      noAgreementSigned: noAgreementFlowEnabled || isNoSignedAgreement || requiresReAcceptanceAfterVerification,
      isNoDataAgreement,
      requiresReAcceptanceAfterVerification,
      isReAcceptanceRequired,
      isNoSignedAgreement,
      isOverdueAgreement,
      noAgreementGenerated,
      canViewAcceptedAgreement,
    };
  }

  static getMsaDpaAgreementRenderInfo(user: UserAdmin): MainAccountAgreementRenderInfo {
    const state = UserAgreementUtils.getMsaAndDpaAgreementState(user);

    if (user.role !== Role.MAIN) {
      return {
        ...state,
        title: '',
        cssClass: '',
      };
    }

    let info: { title: string; cssClass: string };

    if (state.isCustomAgreement) {
      info = {
        title: 'Custom agreement',
        cssClass: 'text-accent-a6',
      };
    } else if (state.isPendingAgreement) {
      info = {
        title: 'Has pending MSA & DPA agreement',
        cssClass: 'text-warning-w2',
      };
    } else if (state.isDeclinedAgreement) {
      info = {
        title: 'Has declined MSA & DPA agreement',
        cssClass: 'text-warning-w3',
      };
    } else if (state.isAcceptedAgreement) {
      info = {
        title: 'Accepted MSA & DPA agreement',
        cssClass: 'text-success-s2',
      };
    } else if (state.isNoDataAgreement) {
      info = {
        title: 'No MSA & DPA agreement generated',
        cssClass: 'text-error-r3',
      };
    } else if (state.requiresReAcceptanceAfterVerification) {
      info = {
        title: 'MSA & DPA re-acceptance required',
        cssClass: 'text-warning-w2',
      };
    } else {
      info = {
        title: 'No MSA & DPA agreement signed',
        cssClass: 'text-error-r3',
      };
    }

    return {
      ...state,
      ...info,
    };
  }

  static renderAgreement(user: UserAdmin): string {
    if (user.role !== Role.MAIN) {
      return '';
    }

    if (!user.customAgreement && !user.clicktermAgreementFlowEnabled) {
      return '';
    }

    const { title, cssClass } = UserAgreementUtils.getMsaDpaAgreementRenderInfo(user);
    return `<i title="${title}" class="icon-doc fs-3 me-0_5 ${cssClass}"></i>`;
  }

  static getMsaDpaAgreementDownloadFileName(user: UserAdmin, agreementActionDate?: string): string {
    const companyName = (user.companyName || 'Company').trim().replace(/[\\/:*?"<>|]+/g, '_');
    const date = UserAgreementUtils.formatDateForFilename(new Date(agreementActionDate || new Date()));
    return `TelQ_MSA_DPA_${companyName}_${date}.pdf`;
  }

  private static formatDateForFilename(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
